// ================================================
// DECISION LOG PANEL
// Feature 4 - Running log of organizational decisions
// ================================================
import React, { useState, useMemo } from 'react';
import { 
  Search, Filter, Download, TrendingUp, Calendar,
  ChevronDown, BookOpen, Target, Zap, DollarSign, 
  Users, Palette, BarChart3
} from 'lucide-react';
import { CHIEF_OF_STAFF_COLORS } from '../focus';
import { UNIQUE_IP_COLORS } from '../focus';
import { decisionLog, formatDate } from '../../data/chiefOfStaffData';

const DecisionLogPanel = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLane, setFilterLane] = useState('all');
  const [filterImpact, setFilterImpact] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showTrends, setShowTrends] = useState(false);

  // Filter decisions
  const filteredDecisions = useMemo(() => {
    return decisionLog.filter(decision => {
      const matchesSearch = searchQuery === '' || 
        decision.decision.toLowerCase().includes(searchQuery.toLowerCase()) ||
        decision.decisionMaker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        decision.context.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesLane = filterLane === 'all' || decision.lane === filterLane;
      const matchesImpact = filterImpact === 'all' || decision.impactTag === filterImpact;
      
      return matchesSearch && matchesLane && matchesImpact;
    });
  }, [searchQuery, filterLane, filterImpact]);

  // Calculate trends
  const trends = useMemo(() => {
    const byLane = {};
    const byMaker = {};
    
    decisionLog.forEach(d => {
      byLane[d.lane] = (byLane[d.lane] || 0) + 1;
      byMaker[d.decisionMaker.name] = (byMaker[d.decisionMaker.name] || 0) + 1;
    });
    
    return {
      byLane: Object.entries(byLane).sort((a, b) => b[1] - a[1]),
      byMaker: Object.entries(byMaker).sort((a, b) => b[1] - a[1]).slice(0, 5),
      totalThisWeek: decisionLog.filter(d => {
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return new Date(d.date) > weekAgo;
      }).length
    };
  }, []);

  const impactConfig = {
    Strategic: { color: '#8B5CF6', icon: Target },
    Operational: { color: '#3B82F6', icon: Zap },
    Financial: { color: '#10B981', icon: DollarSign },
    Cultural: { color: '#F59E0B', icon: Users },
    Creative: { color: '#EC4899', icon: Palette },
    Organizational: { color: '#6366F1', icon: Users }
  };

  const laneColors = {
    vision: '#8B5CF6',
    strategy: '#3B82F6',
    relationships: '#F472B6',
    operations: '#10B981',
    execution: '#06B6D4',
    creative: '#EC4899',
    marketing: '#F59E0B',
    culture: '#EF4444'
  };

  const handleExport = () => {
    alert('This would generate a formatted decision log report for board meetings or documentation');
  };

  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
          <div>
            <h3 className="text-sm font-bold text-white">{decisionLog.length} Decisions</h3>
            <p className="text-xs text-gray-400">{trends.totalThisWeek} this week</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowTrends(!showTrends)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ 
              backgroundColor: showTrends ? CHIEF_OF_STAFF_COLORS.primary : 'transparent',
              color: showTrends ? 'white' : CHIEF_OF_STAFF_COLORS.primary,
              border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}`
            }}
          >
            <BarChart3 className="w-4 h-4" />
            Trends
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/20"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Trends Panel */}
      {showTrends && (
        <div 
          className="p-4 rounded-xl space-y-4"
          style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
        >
          <h4 className="text-xs font-bold text-gray-500 uppercase">Decision Trends</h4>
          
          {/* By Lane */}
          <div className="space-y-2">
            <p className="text-xs text-gray-400">Decisions by Cognitive Lane</p>
            {trends.byLane.slice(0, 5).map(([lane, count]) => (
              <div key={lane} className="flex items-center gap-2">
                <span className="text-xs text-white capitalize w-20">{lane}</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#0D1117' }}>
                  <div 
                    className="h-full rounded-full"
                    style={{ 
                      width: `${(count / decisionLog.length) * 100}%`,
                      backgroundColor: laneColors[lane] || CHIEF_OF_STAFF_COLORS.primary
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-8">{count}</span>
              </div>
            ))}
          </div>
          
          {/* Top Decision Makers */}
          <div className="space-y-2">
            <p className="text-xs text-gray-400">Top Decision Makers</p>
            <div className="flex flex-wrap gap-2">
              {trends.byMaker.map(([name, count]) => (
                <span 
                  key={name}
                  className="text-xs px-2 py-1 rounded"
                  style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary + '20', color: CHIEF_OF_STAFF_COLORS.primaryLight }}
                >
                  {name.split(' ')[0]}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search decisions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg text-sm text-white placeholder-gray-500"
            style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
          />
        </div>
        
        <div className="flex gap-2">
          <select
            value={filterLane}
            onChange={(e) => setFilterLane(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg text-sm text-white"
            style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
          >
            <option value="all">All Lanes</option>
            {Object.keys(laneColors).map(lane => (
              <option key={lane} value={lane} className="capitalize">{lane}</option>
            ))}
          </select>
          <select
            value={filterImpact}
            onChange={(e) => setFilterImpact(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg text-sm text-white"
            style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
          >
            <option value="all">All Impact Types</option>
            {Object.keys(impactConfig).map(impact => (
              <option key={impact} value={impact}>{impact}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Decision List */}
      <div className="space-y-3">
        {filteredDecisions.map((decision) => {
          const impact = impactConfig[decision.impactTag] || impactConfig.Operational;
          const ImpactIcon = impact.icon;
          
          return (
            <div 
              key={decision.id}
              className="p-4 rounded-xl"
              style={{ 
                backgroundColor: '#161B22',
                borderLeft: `4px solid ${laneColors[decision.lane] || CHIEF_OF_STAFF_COLORS.primary}`
              }}
            >
              {/* Date and Impact */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Calendar className="w-3 h-3" />
                  {formatDate(decision.date)}
                </div>
                <span 
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                  style={{ backgroundColor: impact.color + '20', color: impact.color }}
                >
                  <ImpactIcon className="w-3 h-3" />
                  {decision.impactTag}
                </span>
              </div>
              
              {/* Decision Statement */}
              <p className="text-sm font-medium text-white mb-2">
                {decision.decision}
              </p>
              
              {/* Meta info */}
              <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                <span className="flex items-center gap-1">
                  <div 
                    className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white"
                    style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary }}
                  >
                    {decision.decisionMaker.avatar}
                  </div>
                  {decision.decisionMaker.name}
                </span>
                <span>•</span>
                <span>{decision.context}</span>
                <span>•</span>
                <span 
                  className="capitalize px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: laneColors[decision.lane] + '20', color: laneColors[decision.lane] }}
                >
                  {decision.lane}
                </span>
              </div>
              
              {/* Rationale */}
              <p className="text-xs text-gray-500 italic mb-2">
                "{decision.rationale}"
              </p>
              
              {/* Follow-up Actions */}
              {decision.followUpActions && decision.followUpActions.length > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-xs text-gray-500 mb-1">Follow-up actions:</p>
                  <div className="flex flex-wrap gap-1">
                    {decision.followUpActions.map((action, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-2 py-0.5 rounded"
                        style={{ backgroundColor: '#0D1117', color: '#9CA3AF' }}
                      >
                        {action}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredDecisions.length === 0 && (
        <div className="text-center py-8">
          <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm text-gray-500">No decisions match your filters</p>
        </div>
      )}
    </div>
  );
};

export default DecisionLogPanel;
